import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { motion } from 'framer-motion';
import {
  Building2,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Users,
  CreditCard,
  ImageIcon,
  X,
  ChevronLeft,
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { projectId, publicAnonKey } from '../lib/config';
import type { Clinic } from '../lib/types';

interface OrgSettingsProps {
  onNavigate?: (page: string) => void;
}

export function OrgSettings({ onNavigate }: OrgSettingsProps) {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Organization data
  const [org, setOrg] = useState<Clinic | null>(null);
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load organization data
  useEffect(() => {
    if (profile?.clinic_id) {
      loadOrganization();
    } else {
      setLoading(false);
      setError('No organization associated with your account');
    }
  }, [profile?.clinic_id]);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Loading organization for clinic_id:', profile?.clinic_id);

      const { data, error: fetchError } = await supabase
        .from('clinics')
        .select('*')
        .eq('id', profile!.clinic_id)
        .maybeSingle();

      if (fetchError) {
        console.error('Supabase error:', fetchError);
        throw new Error(`Database error: ${fetchError.message}`);
      }

      if (!data) {
        console.error('No clinic found for ID:', profile?.clinic_id);
        throw new Error(`No clinic found with ID ${profile?.clinic_id}. Please contact support to set up your organization.`);
      }

      console.log('Organization loaded:', data);
      setOrg(data);
      setName(data.name);
      setLogoUrl(data.logo_url || null);
    } catch (err: any) {
      console.error('Error loading organization:', err);
      setError(err.message || 'Failed to load organization settings');
      toast.error(err.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  // Handle logo file selection
  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload logo to storage
  const uploadLogo = async (file: File): Promise<string> => {
    try {
      setUploading(true);

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${org!.id}-${Date.now()}.${fileExt}`;
      const filePath = `org-logos/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('clinic-assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('clinic-assets')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      throw new Error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // Save organization settings
  const handleSave = async () => {
    if (!org) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Validate name
      if (!name.trim()) {
        throw new Error('Organization name is required');
      }

      let newLogoUrl = logoUrl;

      // Upload logo if changed
      if (logoFile) {
        newLogoUrl = await uploadLogo(logoFile);
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication required');
      }

      // Call backend to update organization
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-cc125661/org/update`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            org_id: org.id,
            name: name.trim(),
            logo_url: newLogoUrl,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update organization' }));
        throw new Error(errorData.message || errorData.error || 'Failed to save settings');
      }

      const result = await response.json();

      // Update local state
      setOrg({ ...org, name: name.trim(), logo_url: newLogoUrl });
      setLogoUrl(newLogoUrl);
      setLogoFile(null);
      setLogoPreview(null);
      
      setSuccess(true);
      toast.success('Organization settings saved successfully!');

      // Auto-hide success message
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving organization:', err);
      setError(err.message || 'Failed to save organization settings');
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  // No organization state
  if (!org && !loading) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error || 'No organization found. Please contact support.'}
              </AlertDescription>
            </Alert>

            {profile?.clinic_id && (
              <Card className="p-6 glass-card">
                <h3 className="text-lg font-semibold mb-4">Debug Information</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Your User ID:</strong> <code className="text-xs bg-muted px-2 py-1 rounded">{user?.id}</code></p>
                  <p><strong>Your Role:</strong> <code className="text-xs bg-muted px-2 py-1 rounded">{profile?.role}</code></p>
                  <p><strong>Clinic ID:</strong> <code className="text-xs bg-muted px-2 py-1 rounded">{profile?.clinic_id}</code></p>
                  <p className="text-muted-foreground mt-4">
                    This clinic ID does not exist in the database. Please contact support at{' '}
                    <a href="mailto:support@cannabias.tech" className="text-primary hover:underline">
                      support@cannabias.tech
                    </a>
                    {' '}to set up your organization.
                  </p>
                </div>
              </Card>
            )}

            {!profile?.clinic_id && (
              <Card className="p-6 glass-card">
                <h3 className="text-lg font-semibold mb-4">No Organization Assigned</h3>
                <p className="text-muted-foreground mb-4">
                  Your account does not have an organization assigned. To access organization settings, you need to be associated with a clinic.
                </p>
                <p className="text-sm">
                  Please contact support at{' '}
                  <a href="mailto:support@cannabias.tech" className="text-primary hover:underline">
                    support@cannabias.tech
                  </a>
                  {' '}to link your account to a clinic.
                </p>
              </Card>
            )}

            <div className="mt-6">
              <Button
                variant="outline"
                onClick={() => onNavigate && onNavigate('account')}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Account Settings
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center shadow-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl">Settings</h1>
              <p className="text-muted-foreground">
                Manage your profile and organization details
              </p>
            </div>
          </div>
        </motion.div>

        {/* Success Alert */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6"
          >
            <Alert className="border-primary bg-primary/5">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <AlertDescription className="text-primary">
                Settings saved successfully!
              </AlertDescription>
            </Alert>
          </motion.div>
        )}

        {/* Error Alert */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </motion.div>
        )}

        {/* Main Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Card className="p-8 glass-card">
            <div className="space-y-8">
              {/* Profile Information Section */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl mb-1">Profile Information</h2>
                  <p className="text-sm text-muted-foreground">
                    Your account details and role information
                  </p>
                </div>

                <div className="grid gap-6">
                  {/* Email */}
                  <div className="space-y-2">
                    <Label className="text-base">Email</Label>
                    <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <span className="text-base">{user?.email}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Email cannot be changed
                    </p>
                  </div>

                  {/* Display Name */}
                  <div className="space-y-2">
                    <Label className="text-base">Display Name</Label>
                    <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <span className="text-base">{profile?.display_name || 'Not set'}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      This is how your name will appear on reviews
                    </p>
                  </div>

                  {/* Account Role */}
                  <div className="space-y-2">
                    <Label className="text-base">Account Role</Label>
                    <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <span className="text-base capitalize">{profile?.role}</span>
                    </div>
                  </div>

                  {/* Member Since */}
                  <div className="space-y-2">
                    <Label className="text-base">Member Since</Label>
                    <div className="p-3 rounded-lg bg-muted/30 flex items-center gap-3">
                      <span className="text-base">
                        {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-GB', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        }) : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <Separator className="my-8" />

              {/* Organization Details Section */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl mb-1">Organization Details</h2>
                  <p className="text-sm text-muted-foreground">
                    Manage your clinic's public profile and branding
                  </p>
                </div>

                {/* Organization Name */}
                <div className="space-y-2">
                  <Label htmlFor="org-name" className="text-base">
                    Organization Name *
                  </Label>
                <Input
                  id="org-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter organization name"
                  className="text-base h-12"
                  disabled={saving || uploading}
                />
                  <p className="text-sm text-muted-foreground">
                    This name will be displayed on your public profile and QR verification codes.
                  </p>
                </div>

                {/* Logo Upload */}
                <div className="space-y-4">
                <Label className="text-base">Organization Logo</Label>
                
                {/* Current/Preview Logo */}
                <div className="flex items-start gap-6">
                  {(logoPreview || logoUrl) && (
                    <div className="relative">
                      <div className="w-32 h-32 rounded-xl border-2 border-border bg-muted flex items-center justify-center overflow-hidden">
                        <img
                          src={logoPreview || logoUrl || ''}
                          alt="Organization logo"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      {logoPreview && (
                        <button
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center hover:bg-destructive/90 transition-colors"
                          disabled={saving || uploading}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Upload Button */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoSelect}
                      className="hidden"
                      disabled={saving || uploading}
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="w-full sm:w-auto"
                      disabled={saving || uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {logoUrl || logoPreview ? 'Change Logo' : 'Upload Logo'}
                        </>
                      )}
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      Recommended: Square image, at least 256x256px. Max 2MB.
                    </p>
                  </div>
                </div>
                </div>
              </div>

              <Separator className="my-8" />

              {/* Read-Only Fields */}
              <div className="space-y-6">
                <h3 className="text-lg font-medium">Subscription Details</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Seats */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      User Seats
                    </Label>
                    <div className="flex items-baseline gap-3">
                      <span className="text-3xl font-semibold">5</span>
                      <Badge variant="secondary">Active</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      3 of 5 seats currently in use
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onNavigate && onNavigate('org-members')}
                      className="mt-2"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Manage Team Members
                    </Button>
                  </div>

                  {/* Plan */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Current Plan
                    </Label>
                    <div className="flex items-baseline gap-3">
                      <span className="text-3xl font-semibold">Pro</span>
                      <Badge className="gradient-green">Active</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Renews on Dec 31, 2025
                    </p>
                  </div>
                </div>

                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    To change your plan or add more seats, please contact{' '}
                    <a href="mailto:support@cannabias.tech" className="text-primary hover:underline">
                      support@cannabias.tech
                    </a>
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-6 flex items-center justify-between"
        >
          <Button
            variant="ghost"
            onClick={() => onNavigate && onNavigate('clinic-dashboard')}
            disabled={saving || uploading}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || uploading || !name.trim()}
            className="gradient-green hover:opacity-90 transition-opacity min-w-[120px]"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-muted-foreground">
            Need help? Visit our{' '}
            <button
              onClick={() => onNavigate && onNavigate('faq')}
              className="text-primary hover:underline"
            >
              Help Center
            </button>
            {' '}or contact support.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
